import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { getMonthName } from '../../../core/helper/month';
import { getTranslation } from '../../../core/helper/translation';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { ExpandedListViewItemDefinition } from '../../../core/model/expanded-list-view-item';
import { LocalDate } from '../../../core/model/local-date';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { RentalObjectCategory } from '../../../rent/model/const/enums';
import { ArticleGroupService } from '../../../rent/service/data/article-group.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { PropertyService } from '../../../rent/service/data/property.service';
import { ReportService } from '../../service/data/report.service';
import { LanguageService } from '../../../core/service/language.service';
import { canSubmitReportForm } from '../../utility/report-form-validation';

@Component({
    selector: 'app-reasonableness-control',
    templateUrl: './reasonableness-control.component.html',
    styleUrls: ['./reasonableness-control.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReasonablenessControlComponent implements OnDestroy, OnInit {
  constructor(
    private _cdf: ColumnDefinitionFactory,
    private _dataService: ReportService,
    private readonly _languageService: LanguageService,
    private _nativeWindow: NativeWindowService,
    private _companyService: CompanyService,
    readonly articleGroupService: ArticleGroupService,
    private _propertyService: PropertyService,
    private _navigationService: NavigationService,
    fb: FormBuilder,
    fcf: FormControlExFactory
  ) {
    this.firstPeriod = LocalDate.fromDate(new LocalDate(LocalDate.today().year, LocalDate.today().month, 1).addMonths(1));
    this.secondPeriod = new LocalDate(LocalDate.today().year, LocalDate.today().month, 1);

    this.form = fb.group({
      company: fcf.create('COMMON.COMPANY', ''),
      property: fcf.create('COMMON.PROPERTY', ''),
      articleGroups: fcf.create('ARTICLE_GROUP.ARTICLE_GROUP', []),
      period1: fcf.create('REPORT.GET_INVOICED_MONTH1', this.firstPeriod.toISOString()),
      period2: fcf.create('REPORT.GET_INVOICED_MONTH2', this.secondPeriod.toISOString()),
    });

    this.columnDefinitions = [
      this._cdf.createMoney('COMMON.INVOICED_MONTH1', 'invoiceAmountPeriod1'),
      this._cdf.createMoney('COMMON.INVOICED_MONTH2', 'invoiceAmountPeriod2'),
    ];
  }

  ngOnInit() {
    this._companyService.getList().subscribe(data => {
      this.companies = [{ name: 'ALL_TEXT', value: '' }];
      data.forEach(item => {
        this.companies.push({ name: item.toString(), value: item.id });
      });

      if (this.companies.length >= 2) {
        this.form.controls['company'].setValue(this.companies[1].value);
      }
    });

    this._propertyService.getList().subscribe(data => {
      this.realEstateProperties = [{ name: 'ALL_TEXT', value: '' }];
      data.forEach(item => {
        this.realEstateProperties.push({ name: item.toString(), value: item.id });
      });
    });

    this._dataService.getPeriodFilterDefinition().subscribe(definition => {
      let tran = this._languageService.getCurrentTranslations();
      this.monthSelection = definition.dateLimits.map(x => {
        return {
          name: `${x.month ? getTranslation(tran, getMonthName(x.month)) : ''} ${x.year ? x.year + ' ' : ''}`,
          value: new LocalDate(x.year, x.month, 1).toISOString(),
        };
      });
    });
  }

  ngOnDestroy(): void {
    if (this._subscription) this._subscription.unsubscribe();
  }

  columnDefinitions: ColumnDefinition[] = [];
  monthSelection: { name: string; value: string }[] = [];
  companies: { name: string; value: string }[];
  realEstateProperties: { name: string; value: string }[];
  firstPeriod: LocalDate;
  secondPeriod: LocalDate;
  protected expandAllRows: boolean = false;

  exportExcel(): void {
    if (!canSubmitReportForm(this.form)) return;

    if (this._subscription) this._subscription.unsubscribe();

    this._subscription = this._dataService.getReasonablenessControlReportExcel(this.form.value).subscribe(file => {
      this._nativeWindow.save(file);
    });
  }

  onPrintPages() {
    if (!canSubmitReportForm(this.form)) return;
    window.print();
  }

  private _getPath(category: RentalObjectCategory) {
    let segment = '';
    switch (category) {
      case RentalObjectCategory.Dwelling:
      case RentalObjectCategory.Parking:
      case RentalObjectCategory.Premises:
        segment = RentalObjectCategory[category].toLowerCase();
        break;

      case RentalObjectCategory.Other:
        segment = 'other-rental-object';
        break;

      default:
        throw new Error(`The specified contract category is not supported. Category: ${category}`);
    }
    return segment;
  }

  onExpandedContentRowClicked(e: { context: any; event: any }): void {
    this._navigationService.navigate(
      ['my-property', this._getPath(e.context.rentalObjectCategory), 'edit', e.context.rentalObjectId],
      null,
      e.event.ctrlKey
    );
  }

  form: FormGroup;
  dataContext: ExpandedListViewItemDefinition[] = [];

  private _subscription: Subscription;

  onSubmit() {
    if (!canSubmitReportForm(this.form)) return;
    this.expandAllRows = false;

    if (this._subscription) this._subscription.unsubscribe();

    this._subscription = this._dataService.getReasonablenessControlReport(this.form.value).subscribe(data => {
      this.dataContext = data
        ? data.map(reportData => {
            const model = new ExpandedListViewItemDefinition();
            model.caption = reportData.rentalObjectCaption;
            model.columnDefinitions = [
              this._cdf.create('ARTICLE.EDIT_HEADER', 'articleCaption'),
              this._cdf.createMoney('COMMON.INVOICED_MONTH1', 'rowAmountPeriod1'),
              this._cdf.createMoney('COMMON.INVOICED_MONTH2', 'rowAmountPeriod2'),
            ];

            model.dataContext = reportData;
            model.itemsSource = reportData.reasonablenessControlLevel2Models.map(x => {
              return {
                rentalObjectId: x.rentalObjectId,
                rentalObjectCategory: x.rentalObjectCategory,
                articleCaption: x.articleCaption,
                rowAmountPeriod1: x.rowAmountPeriod1,
                rowAmountPeriod2: x.rowAmountPeriod2,
              };
            });
            return model;
          })
        : [];
    });
  }

  toggleExpandAllRows() {
    this.expandAllRows = !this.expandAllRows;
  }
}
